// apps/client/src/utils/bonusTiers.ts
// Scaled bonus tiers for performance targets — stepped (highest tier met wins).
// All monetary values in integer cents.

export interface BonusTier {
  thresholdCents: number;
  bonusCents: number;
}

export interface PayableBonusResult {
  /** Bonus payable in integer cents — the bonus from the highest tier whose
   *  threshold has been met. Zero when no tier is met. */
  payable: number;
  /** Index into tiers[] of the highest met tier, or -1 if none met. */
  metTierIndex: number;
  /** Index of the next (unmet) tier, or -1 if all tiers met. */
  nextTierIndex: number;
}

/**
 * Compute the payable bonus given the broker's current measured value.
 *
 * Rule: stepped — the broker is paid the bonus from the HIGHEST tier whose
 * threshold has been met. Lower tiers are NOT cumulative.
 *
 * Example: tiers = [{$5M, $1k}, {$7.5M, $2.5k}, {$10M, $5k}]; current = $8M
 * → metTierIndex=1, payable=$2,500.
 *
 * Caller invariant: `tiers` MUST be non-empty and pre-sorted ascending by
 * thresholdCents. The helper does not sort or validate — that's the
 * responsibility of the read-side normalization layer (useBrokerTargets).
 */
export function payableBonusCents(
  currentValueCents: number,
  tiers: BonusTier[]
): PayableBonusResult {
  if (tiers.length === 0) {
    throw new Error('payableBonusCents: tiers must contain at least one entry');
  }

  let metIdx = -1;
  for (let i = 0; i < tiers.length; i++) {
    if (currentValueCents >= tiers[i].thresholdCents) {
      metIdx = i;
    } else {
      break; // tiers sorted ascending — first miss means no further matches
    }
  }

  return {
    payable: metIdx === -1 ? 0 : tiers[metIdx].bonusCents,
    metTierIndex: metIdx,
    nextTierIndex: metIdx + 1 < tiers.length ? metIdx + 1 : -1,
  };
}

/**
 * Normalize a legacy single-bonus target shape to the tiered shape.
 *
 * Used by the read-side mapper in useBrokerTargets.ts to ensure every
 * PerformanceTarget surfaces a non-empty `bonusTiers` array regardless of
 * whether the Firestore doc was written with v1 (single bonus) or v2 (tiered)
 * schema. Idempotent: if a `bonusTiers` array is already present and
 * non-empty, returns it sorted ascending by threshold.
 */
export function normalizeBonusTiers(
  legacyThresholdCents: number,
  legacyBonusCents: number,
  bonusTiers: BonusTier[] | undefined | null
): BonusTier[] {
  if (Array.isArray(bonusTiers) && bonusTiers.length > 0) {
    return [...bonusTiers].sort((a, b) => a.thresholdCents - b.thresholdCents);
  }
  return [{ thresholdCents: legacyThresholdCents, bonusCents: legacyBonusCents }];
}
